import { useEffect, useState } from "react";

import { AlertTriangle, Search } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import type { KbRagSearch } from "..";
import { useKbSearch } from "../api";
import { ArticleHit } from "./ArticleHit";
import { CategorySelect } from "./CategorySelect";

const EXAMPLES = [
  "how do I stop text messages going out at night",
  "connect a Google Business Profile for review requests",
  "what happens to loyalty points when a customer is deleted",
];

export function SearchTab({
  search,
  setSearch,
}: {
  search: KbRagSearch;
  setSearch: (next: Partial<KbRagSearch>) => void;
}) {
  const [draft, setDraft] = useState(search.q ?? "");

  const { data, isFetching, error } = useKbSearch({
    query: search.q ?? "",
    category: search.cat,
    limit: 10,
  });

  useEffect(() => {
    setDraft(search.q ?? "");
  }, [search.q]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = draft.trim();
    if (!q) return;
    setSearch({ q });
  };

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask it the way a customer would — a question, not a keyword"
          className="min-w-64 flex-1"
        />
        <CategorySelect
          value={search.cat}
          onChange={(cat) => setSearch({ cat })}
          disabled={isFetching}
        />
        <Button type="submit" disabled={isFetching || !draft.trim()}>
          <Search className="mr-2 h-4 w-4" />
          {isFetching ? "Searching…" : "Search"}
        </Button>
      </form>

      {!search.q ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            This is the same retrieval the Cinch AI agent runs before it answers
            a how-to question. One result per article, best-matching section
            first. If the right article is missing or ranks low here, the agent
            will miss it too.
          </p>
          <div className="mt-3 flex flex-wrap justify-center gap-2">
            {EXAMPLES.map((ex) => (
              <Button
                key={ex}
                variant="outline"
                size="sm"
                onClick={() => setSearch({ q: ex })}
              >
                {ex}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {isFetching ? (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : null}

      {error ? (
        <Alert variant="danger">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Search failed</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      ) : null}

      {data && !isFetching ? (
        <>
          <p className="text-sm text-muted-foreground">
            {data.hits.length} article{data.hits.length === 1 ? "" : "s"} in{" "}
            {data.took_ms} ms
            {search.cat ? ` · ${search.cat}` : ""} · embedded with{" "}
            <span className="font-mono text-xs">{data.embed_model}</span>
          </p>
          <div className="flex flex-col gap-2">
            {data.hits.map((hit, i) => (
              <ArticleHit
                key={`${hit.document_id}-${i}`}
                hit={hit}
                index={i + 1}
                onOpenArticle={(id) =>
                  setSearch({ tab: "articles", article: id })
                }
              />
            ))}
          </div>
          {data.hits.length === 0 ? (
            <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              No matches. Try phrasing it as the customer would, or clear the
              category filter. If it still finds nothing, that is a gap worth an
              article.
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
